/**
 * EntityLinksPanel — Iter 290zb.
 *
 * Generic many-to-many link UI for the JobCard ↔ Picking ↔ SalesDoc
 * triangle. Renders one card per `linkTo` type with:
 *   • list of currently-linked items (chip + status + open + unlink)
 *   • search-then-pick inline panel to attach an existing item
 *
 * Uses `/api/entity-links/connect|disconnect|{entity_type}/{id}` so
 * transitive propagation happens automatically server-side (e.g. linking
 * a Picking to a Sales Order while it's already linked to a JC will
 * also link the JC to the Sales Order on the next refresh).
 *
 * Why not three bespoke blocks? Because the picking/sales-doc/jobcard
 * link UI is symmetric — same chip layout, same search, same actions.
 * Branching by `linkTo` keeps it DRY and means new linking edges (e.g.
 * Picking ↔ Order Request) cost ~5 LOC to add.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Link2, X, AlertTriangle, Loader2, ArrowRight, Plus, Search,
  ClipboardList, FolderKanban, FileSignature,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "./ui/sonner";

// Display + routing config per entity type.
// Iter 395b — SECURITY: all search fns now accept an owner customer_id
// so the picker only surfaces docs belonging to that customer. Passing
// null/undefined = unscoped (used only when the owner itself has no
// customer scope, e.g. an internal-only picking list). Backend
// /entity-links/connect enforces the same rule server-side.
const TYPE_META = {
  jobcard: {
    label: "Job cards",
    singularShort: "JC",
    icon: ClipboardList,
    accent: "#1FB6C1",
    route: (r) => `/jobcards/${r.id}`,
    search: async (q, customerId) => {
      const params = { q, limit: 40 };
      if (customerId) params.customer_id = customerId;
      const { data } = await api.get(`/jobcards`, { params });
      return data?.items || data || [];
    },
  },
  picking: {
    label: "Picking lists",
    singularShort: "Pick",
    icon: FolderKanban,
    accent: "#A78BFA",
    route: (r) => `/picking/lists/${r.id}`,
    search: async (q, customerId) => {
      const params = { limit: 100 };
      if (customerId) params.customer_id = customerId;
      const { data } = await api.get(`/picking/lists`, { params });
      const all = data?.items || [];
      const qq = (q || "").trim().toLowerCase();
      if (!qq) return all;
      return all.filter((l) => `${l.name || ""} ${l.short_id || ""}`.toLowerCase().includes(qq));
    },
  },
  sales_doc: {
    label: "Sales orders",
    singularShort: "SO",
    icon: FileSignature,
    accent: "#10B981",
    route: (r) => `/sales-documents/${r.id}/edit`,
    search: async (q, customerId) => {
      const params = { category: "sales_order", q, limit: 40 };
      if (customerId) params.customer_id = customerId;
      const { data } = await api.get(`/sales-documents`, { params });
      return data?.items || data || [];
    },
  },
};

/**
 * Props:
 *   - entityType: "jobcard" | "picking" | "sales_doc"
 *   - entityId: string
 *   - linkTo: array of types to render (defaults to "the other two")
 *   - onChanged: optional callback after any link mutation
 *   - className: optional extra classes for the outer card
 */
export default function EntityLinksPanel({ entityType, entityId, linkTo, onChanged, className = "", showPullActions = false }) {
  const [links, setLinks] = useState({});
  const [loading, setLoading] = useState(false);
  // Iter 395b — owner's customer_id. Used to scope the picker search
  // (see TYPE_META.search). Fetched from the owner's own detail
  // endpoint. If null, the picker is unscoped (only for docs without
  // a customer, e.g. internal picking lists).
  const [ownerCustomerId, setOwnerCustomerId] = useState(null);

  const targets = useMemo(
    () => (linkTo && linkTo.length
      ? linkTo
      : Object.keys(TYPE_META).filter((k) => k !== entityType)),
    [linkTo, entityType],
  );

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/entity-links/${entityType}/${entityId}`);
      setLinks(data || {});
    } catch {
      setLinks({});
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  // Owner customer_id lookup. Different endpoints per entity — we hit
  // the detail URL and read customer_id. Runs once per (entityType, id).
  useEffect(() => {
    let cancelled = false;
    const fetchCust = async () => {
      if (!entityId) return;
      const url = entityType === "jobcard" ? `/jobcards/${entityId}`
        : entityType === "picking" ? `/picking/lists/${entityId}`
        : entityType === "sales_doc" ? `/sales-documents/${entityId}`
        : null;
      if (!url) return;
      try {
        const { data } = await api.get(url);
        if (!cancelled) setOwnerCustomerId(data?.customer_id || null);
      } catch {
        if (!cancelled) setOwnerCustomerId(null);
      }
    };
    fetchCust();
    return () => { cancelled = true; };
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const connect = async (otherType, otherId) => {
    try {
      await api.post(`/entity-links/connect`, {
        a_type: entityType, a_id: entityId,
        b_type: otherType, b_id: otherId,
      });
      // Iter 395 — when linking a Sales Order to a picking slip, users
      // ALWAYS want the SO lines pulled in as picking rows too (the
      // whole reason to link the SO is to work off it). Auto-pull now
      // so it's a single click. Idempotent — if lines were already
      // pulled, this is a no-op.
      if (showPullActions && entityType === "picking" && otherType === "sales_doc") {
        try {
          const { data } = await api.post(
            `/picking/lists/${entityId}/pull-from-sales-doc`,
            { sales_doc_id: otherId },
          );
          const added = data?.added ?? 0;
          if (added > 0) {
            toast.success(`Linked & pulled ${added} row${added === 1 ? "" : "s"} from the sales order.`);
          } else {
            toast.success("Sales order linked. No new rows to pull (already on the slip).");
          }
        } catch {
          toast.success("Linked — but items could not be pulled automatically. Use the Pull button.");
        }
      } else {
        toast.success("Linked.");
      }
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed.");
    }
  };

  const disconnect = async (otherType, otherId) => {
    // Iter 290zm — when unlinking a JC from a picking list (in either
    // direction), pre-flight a count of rows on that list currently
    // assigned to the JC. We warn before destroying the assignments.
    let preflightMsg = "Unlink this connection?";
    try {
      let jcId = null, lid = null;
      if (entityType === "jobcard" && otherType === "picking") {
        jcId = entityId; lid = otherId;
      } else if (entityType === "picking" && otherType === "jobcard") {
        jcId = otherId; lid = entityId;
      }
      if (jcId && lid) {
        const { data } = await api.get(`/picking/lists/${lid}/assigned-rows`, { params: { jobcard_id: jcId } });
        const n = Number(data?.count || 0);
        if (n > 0) {
          preflightMsg =
            `${n} row${n === 1 ? "" : "s"} on this picking list ${n === 1 ? "is" : "are"} currently assigned to this job card. ` +
            `Unlinking will unassign ${n === 1 ? "it" : "them"} (the row${n === 1 ? "" : "s"} will stay on the picking list but disappear from this job card's materials). Continue?`;
        }
      }
    } catch {
      // Pre-flight failure is non-fatal — fall back to generic prompt.
    }
    if (!window.confirm(preflightMsg)) return;
    try {
      // Iter 410.4 · Optimistically remove the chip so the UI shows
      // instant feedback even before the server round-trip completes.
      // If the request fails, the follow-up `load()` will restore it.
      setLinks((prev) => {
        const next = { ...prev };
        if (Array.isArray(next[otherType])) {
          next[otherType] = next[otherType].filter((x) => x.id !== otherId);
        }
        return next;
      });
      const { data } = await api.post(`/entity-links/disconnect`, {
        a_type: entityType, a_id: entityId,
        b_type: otherType, b_id: otherId,
      });
      const n = Number(data?.rows_unassigned || 0);
      if (n > 0) toast.success(`Unlinked. ${n} row${n === 1 ? "" : "s"} unassigned from the job card.`);
      else toast.success("Unlinked.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Unlink failed.");
      // Restore chip on error.
      await load();
    }
  };

  if (loading && Object.keys(links).length === 0) {
    return (
      <div className={`hv-card p-4 ${className}`} data-testid={`entity-links-${entityType}-${entityId}-loading`}>
        <div className="text-xs text-[#647087] flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading links…
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`} data-testid={`entity-links-${entityType}-${entityId}`}>
      {targets.map((t) => (
        <LinkSection
          key={t}
          otherType={t}
          rows={links[t] || []}
          existingIds={(links[t] || []).map((r) => r.id)}
          ownerType={entityType}
          ownerId={entityId}
          ownerCustomerId={ownerCustomerId}
          onConnect={(rid) => connect(t, rid)}
          onDisconnect={(rid) => disconnect(t, rid)}
          showPullActions={showPullActions}
          onPulled={load}
        />
      ))}
    </div>
  );
}

function LinkSection({ otherType, rows, existingIds, ownerType, ownerId, ownerCustomerId = null, onConnect, onDisconnect, showPullActions = false, onPulled }) {
  const meta = TYPE_META[otherType];
  const Icon = meta.icon;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [available, setAvailable] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pullingId, setPullingId] = useState(null);

  // Iter 395 — pull items from a linked Sales Order into this picking
  // list. Idempotent (accumulates qty, dedups on name/SKU). We do NOT
  // expose a "pull" for linked Job Cards here because JC materials are
  // ALREADY picking rows (stamped with source_jobcard_id) — there's
  // nothing separate to pull.
  const canPull = showPullActions
    && ownerType === "picking"
    && otherType === "sales_doc";
  const pullOne = async (row) => {
    setPullingId(row.id);
    try {
      const { data } = await api.post(`/picking/lists/${ownerId}/pull-from-sales-doc`, { sales_doc_id: row.id });
      const added = data?.added ?? 0;
      toast.success(`Pulled ${added} row${added === 1 ? "" : "s"} from ${row.short_id || row.__label || "sales order"}.`);
      onPulled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull failed.");
    } finally {
      setPullingId(null);
    }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setBusy(true);
    try {
      const items = await meta.search(search, ownerCustomerId);
      setAvailable((items || []).filter((i) => !existingIds.includes(i.id)));
    } catch {
      setAvailable([]);
    } finally {
      setBusy(false);
    }
  };

  const refilter = async (q) => {
    setSearch(q);
    setBusy(true);
    try {
      const items = await meta.search(q, ownerCustomerId);
      setAvailable((items || []).filter((i) => !existingIds.includes(i.id)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hv-card p-3" data-testid={`entity-link-section-${ownerType}-${otherType}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h4 className="font-display font-bold text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: meta.accent }} />
          {meta.label}
          <span className="text-xs text-[#647087] font-normal">({rows.length})</span>
        </h4>
        <button
          type="button"
          onClick={openPicker}
          className="hv-btn hv-btn-ghost text-[11px]"
          data-testid={`entity-link-add-${otherType}`}
        >
          <Plus className="w-3 h-3" /> Link {meta.singularShort}
        </button>
      </div>

      {rows.length === 0 && (
        <div className="text-[11px] text-[#647087] italic py-1.5">No linked {meta.label.toLowerCase()} yet.</div>
      )}

      {rows.length > 0 && (
        <ul className="space-y-1.5" data-testid={`entity-link-list-${otherType}`}>
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-2 p-2 rounded border border-white/5 bg-white/[0.02]"
              data-testid={`entity-link-row-${otherType}-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                  {r.short_id && (
                    <span
                      className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border"
                      style={{ borderColor: `${meta.accent}66`, color: meta.accent, background: `${meta.accent}1a` }}
                    >
                      {r.short_id}
                    </span>
                  )}
                  {r.__status && (
                    <span className="text-[9px] uppercase tracking-wider text-[#647087] font-mono">
                      {String(r.__status).replace(/_/g, " ")}
                    </span>
                  )}
                  {r.audience === "internal" && (
                    <span className="text-[9px] uppercase tracking-wider text-rose-300 font-mono" title="Internal / DNU">DNU</span>
                  )}
                </div>
                <div className="text-xs text-white truncate">{r.__label}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {canPull && (
                  <button
                    type="button"
                    onClick={() => pullOne(r)}
                    disabled={pullingId === r.id}
                    className="hv-btn hv-btn-ghost text-[11px] text-emerald-300 disabled:opacity-50"
                    title="Pull all line items from this sales order into the picking slip (idempotent)"
                    data-testid={`entity-link-pull-${otherType}-${r.id}`}
                  >
                    {pullingId === r.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Plus className="w-3 h-3" />
                    )}
                    <span>{pullingId === r.id ? "Pulling…" : "Pull items"}</span>
                  </button>
                )}
                <Link
                  to={meta.route(r)}
                  className="hv-btn hv-btn-ghost text-[11px]"
                  title="Open"
                  data-testid={`entity-link-open-${otherType}-${r.id}`}
                >
                  Open <ArrowRight className="w-3 h-3" />
                </Link>
                <button
                  type="button"
                  onClick={() => onDisconnect(r.id)}
                  className="hv-btn hv-btn-ghost text-[11px] text-rose-300"
                  title="Unlink"
                  data-testid={`entity-link-unlink-${otherType}-${r.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pickerOpen && (
        <div className="mt-3 border border-white/10 rounded p-3 bg-black/30 space-y-2" data-testid={`entity-link-picker-${otherType}`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: meta.accent }}>
              Link an existing {meta.singularShort}
            </div>
            <button type="button" onClick={() => setPickerOpen(false)} className="text-[#647087] hover:text-white text-xs">close</button>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#647087]" />
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(e) => refilter(e.target.value)}
              placeholder={`Search ${meta.label.toLowerCase()}…`}
              className="hv-input w-full pl-7 text-xs"
              data-testid={`entity-link-search-${otherType}`}
            />
          </div>
          {busy && <div className="text-[11px] text-[#647087] flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
          {!busy && available.length === 0 && (
            <div className="text-[11px] text-amber-200 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> No matching {meta.label.toLowerCase()} found.
            </div>
          )}
          {!busy && available.length > 0 && (
            <ul className="max-h-60 overflow-auto space-y-1">
              {available.slice(0, 50).map((it) => {
                const label = it.title || it.name || it.document_number || it.short_id || it.id?.slice(0, 8);
                const sub = it.short_id || it.document_number || it.id?.slice(0, 8);
                const status = it.status || it.phase || it.category || "";
                return (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => { onConnect(it.id); setPickerOpen(false); }}
                      className="w-full text-left p-2 rounded border border-white/5 bg-[#0a0b0e] hover:bg-white/[0.04] flex items-center justify-between gap-2"
                      data-testid={`entity-link-picker-row-${otherType}-${it.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-xs text-white truncate">{label}</div>
                        <div className="text-[10px] text-[#647087] font-mono">
                          {sub}{status && ` · ${String(status).replace(/_/g, " ")}`}
                        </div>
                      </div>
                      <Link2 className="w-3 h-3" style={{ color: meta.accent }} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
